import { FC, useCallback, useEffect } from 'react';
import { useFormContext, Controller, useWatch, get } from 'react-hook-form';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { Colors, FontSize, FontWeight, Spacing } from '../../../assets/GlobalStyle/Variables';
import { ValidationRule } from './validate';

type Option = {
  value: string | number;
  label: string;
  description?: string;
};

type ControlSelectProps = {
  name: string;
  label: string;
  id: string;
  options: Option[];
  isMulti?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  autoTransform?: boolean;
  validate?: ValidationRule[]
};

export const ControlSelect: FC<ControlSelectProps> = (props) => {
  const {
    name,
    id,
    label,
    options,
    disabled = false,
    isMulti = false,
    placeholder = 'Vyberte...',
    required = false,
    autoTransform = false,
    validate
  } = props;
  const methods = useFormContext();

  const validators = validate ?? [];

  const validation = useCallback(async (value: unknown) => {
    const formValues = methods.getValues() as Record<string, unknown>;

    for (const rule of validators) {
      const result = await rule(value, formValues);
      if (result !== true) {
        return result;
      }
    }

    return true;
  }, [validators, methods]);

  const transformValueToOption = (value: any): Option | Option[] | null => {
    if (!autoTransform || value === null || value === undefined) return value;

    if (isMulti && Array.isArray(value)) {
      return value
        .map((val) => options.find((opt) => opt.value === val))
        .filter((opt): opt is Option => opt !== undefined);
    }

    if (typeof value === 'number' || typeof value === 'string') {
      return options.find((opt) => opt.value === value) || null;
    }

    return value;
  };

  const transformOptionToValue = (option: Option | Option[] | null): any => {
    if (!autoTransform || !option) return option;

    if (isMulti && Array.isArray(option)) {
      return option.map((opt) => opt.value);
    }

    if (option && typeof option === 'object' && 'value' in option) {
      return option.value;
    }

    return option;
  };
  const hasError = !!get(methods.formState.errors, name);
  const error = get(methods.formState.errors, name);

  const selectStyles = {
    control: (base: any, { isDisabled }: { isDisabled: boolean }) => ({
      ...base,
      borderRadius: 0,
      height: 42,
      borderStyle: 'none',
      boxShadow: 'none',
      backgroundColor: isDisabled ? Colors.disabled : 'transparent',
      '&:hover': {
        borderColor: hasError ? Colors.error : Colors.disabledHover
      }
    }),
    option: (base: any) => ({
      ...base,
      padding: `${Spacing(2)} ${Spacing(6)} 0`,
      margin: 0
    })
  };

  const filterOption = (option: any, inputValue: string) => {
    const search = inputValue.toLowerCase();

    const label = option.label?.toLowerCase() || '';
    const description = option.data?.description?.toLowerCase() || '';

    return label.includes(search) || description.includes(search);
  };

  // Custom options rendering with description if set in options
  const Option = (props: any) => {
    return (
      <components.Option {...props}>
        <StyledOptionWrapper>
          <StyledOptionLabel>{props.data.label}</StyledOptionLabel>
          {props.data.description && (
            <StyledOptionDescription>{props.data.description}</StyledOptionDescription>
          )}
        </StyledOptionWrapper>
      </components.Option>
    );
  };

  const toggle = useWatch({ control: methods.control, name: name });

  useEffect(() => {
    if (!toggle) {
      clearFormValues();
    }
  }, [toggle]);

  const clearFormValues = () => {
    methods.setValue(name, []);
    methods.reset(methods.getValues(), { keepDefaultValues: true });
  };

  return (
    <Wrapper>
      {label && (
        <StyledLabel htmlFor={id}>
          {label}:{required && ' *'}
        </StyledLabel>
      )}
      <Controller
        name={name}
        control={methods.control}
        rules={{ required: required && 'Povinné pole', validate: validation }}
        render={({ field }) => (
          <StyledSelectWrapper>
            <StyledControlSelect
              $hasError={hasError}
              isClearable
              options={options}
              filterOption={filterOption}
              isDisabled={disabled}
              isMulti={isMulti ? true : false}
              styles={selectStyles}
              placeholder={placeholder}
              value={transformValueToOption(field.value)}
              onChange={(option) =>
                field.onChange(transformOptionToValue(option as Option | Option[] | null))
              }
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              components={{ Option }}
            />
            {error?.message && <span role="alert">{String(error.message)}</span>}
          </StyledSelectWrapper>
        )}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  margin-bottom: ${Spacing(8)};
`;

const StyledSelectWrapper = styled.div`
  width: 100%;

  span[role='alert'] {
    display: block;
    margin: 2px 0;
    color: ${Colors.error};
    font-size: ${FontSize.small};
  }
`;

const StyledControlSelect = styled(Select)<{ $hasError?: boolean }>`
  margin: 0px;
  border: 1px solid ${(props) => (props.$hasError ? Colors.error : props.isDisabled ? 'transparent' : Colors.disabledHover)};
  line-height: 30px;
  height: 42px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 0px;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
  background-color: ${Colors.white};

  &:focus {
    outline: none;
    border: 1px solid ${(props) => (props.$hasError ? Colors.error : props.isDisabled ? 'transparent' : Colors.disabledHover)};
  }
`;

const StyledLabel = styled.label`
  margin: 0;
  color: ${Colors.grey};
  font-weight: ${FontWeight.strong};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  min-width: 200px;
`;

const StyledOptionWrapper = styled.label`
  display: flex;
  flex-flow: column;
`;

const StyledOptionLabel = styled.span`
  font-size: ${FontSize.normal};
`;

const StyledOptionDescription = styled.span`
  font-size: ${FontSize.medium};
`;
